import React, { useEffect, useState } from 'react';

import {
  Badge,
  Box,
  Button,
  Container,
  Grid,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { fetchGet } from '@par/shared/api/common';
import { StyledPaperItem } from '../../common/mui-components';
import { StarterKitDialog } from '../../dialog/StarterKitDialog';
import { getAbbreviatedStarterKitName, getStarterKitIcon } from './constants';
import {
  StarterKit,
  StarterKitGamesAndStats,
  StarterKitUserStats,
} from './types';
import { CreateGameConfig } from '@par/shared/types/game';
import { Profile } from '@par/shared/types/player';
import { Maintenance } from '../room/types';

type PropTypes = {
  handleStarterKitPlay: () => void;
  maintenance: Maintenance;
  profile: Profile;
  setCreateGameConfig: (opts: CreateGameConfig) => void;
};

export const StarterKits = ({
  handleStarterKitPlay,
  maintenance,
  profile,
  setCreateGameConfig,
}: PropTypes) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [starterKits, setStarterKits] = useState<StarterKit[]>();
  const [openSKDialog, setOpenSKDialog] = useState(false);
  const [selectedSK, setSelectedSK] = useState<StarterKit>();
  const [userStats, setUserStats] = useState<StarterKitUserStats>();
  const [selectedUserStats, setSelectedUserStats] =
    useState<StarterKitGamesAndStats[]>();

  useEffect(() => {
    fetchStarterKits();
    fetchStarterKitUserStats();
  }, []);

  const fetchStarterKits = async () => {
    const res = await fetchGet('/starter-kits/all');
    setStarterKits(res);
  };

  const fetchStarterKitUserStats = async () => {
    const res: StarterKitUserStats = await fetchGet(
      `/starter-kits/user-stats/${profile.id}`
    );
    if (!res.profileId) return;
    setUserStats(res);
  };

  const handleStarterKitClick = (starterKit: StarterKit) => {
    setSelectedSK(starterKit);
    setOpenSKDialog(true);
    if (!userStats) return;
    const selectedUserStats = userStats.stats.filter(
      (s) => s.starterKitSet === starterKit.starterKitSet
    );
    setSelectedUserStats(selectedUserStats);
  };

  const getTotalGamesPlayed = (starterKit: StarterKit) => {
    if (!userStats) return;
    const stats = userStats.stats;
    const starterKitStats = stats.filter(
      (s) => s.starterKitSet === starterKit.starterKitSet
    );
    if (!starterKit) return 0;
    let gamesCount = 0;
    for (const st of starterKitStats) {
      gamesCount = gamesCount + st.games.length;
    }
    return gamesCount;
  };

  return (
    <>
      {selectedSK ? (
        <StarterKitDialog
          close={() => setOpenSKDialog(false)}
          handleStarterKitPlay={handleStarterKitPlay}
          maintenance={maintenance}
          open={openSKDialog}
          profile={profile}
          setCreateGameConfig={setCreateGameConfig}
          starterKit={selectedSK}
          userStats={selectedUserStats}
        />
      ) : null}
      <Container
        sx={{
          paddingBottom: '100px',
        }}
      >
        <Box width={'100%'} display="flex" justifyContent={'center'}>
          <Box position="relative">
            <Typography
              variant="h4"
              textAlign={'center'}
              letterSpacing={5}
              mb={2}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                fontWeight: 600,
                width: 'fit-content',
                backgroundImage:
                  'linear-gradient(to left, violet, indigo, blue, green, yellow, orange, red)',
                color: 'transparent',
                WebkitBackgroundClip: 'text',
              }}
            >
              STARTER KITS
            </Typography>
            <Typography
              variant="h4"
              textAlign={'center'}
              letterSpacing={5}
              mb={2}
              sx={{
                color: 'transparent',
                fontWeight: 600,
                width: 'fit-content',
                textShadow: `0 0 1px white,
                              0 0 1px white,
                              0px 0px 4px white`,
              }}
            >
              STARTER KITS
            </Typography>
          </Box>
        </Box>
        <Grid container spacing={2} className="CONTAINER-MAIN">
          <Grid item xs={12} sm={12} className="CONTAINER-TOP">
            <Grid container spacing={2}>
              {starterKits?.map((sk) => (
                <Grid item xs={6} sm={4} md={3} className="DESCRIPTION">
                  <StyledPaperItem>
                    <Badge
                      badgeContent={
                        getTotalGamesPlayed(sk) ? (
                          <Typography
                            sx={{
                              border: '1px solid',
                              borderColor: theme.palette.info.main,
                              borderRadius: 2,
                              paddingX: 1,
                              textShadow: `0 0 2px ${theme.palette.info.main},
                         0 0 2px ${theme.palette.info.main},
                         0px 0px 8px ${theme.palette.info.main}`,
                            }}
                          >
                            {getTotalGamesPlayed(sk)}
                          </Typography>
                        ) : null
                      }
                      sx={{
                        width: '100%',
                      }}
                    >
                      <Button
                        fullWidth={true}
                        onClick={() => handleStarterKitClick(sk)}
                        startIcon={getStarterKitIcon(sk.starterKitSet, 'start')}
                        // endIcon={getStarterKitIcon(sk.starterKitSet, 'end')}
                      >
                        {getAbbreviatedStarterKitName(sk.starterKitSet) ||
                          sk.starterKitName}
                      </Button>
                    </Badge>
                  </StyledPaperItem>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};
